import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { authFetch } from '../services/api';

export default function AdminAds() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchConfig(); }, []);

  async function fetchConfig() {
    setLoading(true);
    try {
      const adminRes = await authFetch('/api/ads/admin-config');
      if (adminRes.status === 401 || adminRes.status === 403) {
        toast.error('Admin access required');
        return;
      }
      const full = await adminRes.json();
      // Server normalises live to a list, but be defensive against stale caches.
      if (!Array.isArray(full.live)) {
        full.live = full.live ? [full.live] : [];
      }
      setConfig(full);
    } catch {
      toast.error('Failed to load config');
    } finally {
      setLoading(false);
    }
  }

  async function handleApprove(action) {
    try {
      const res = await authFetch('/api/ads/approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Action failed'); return; }
      toast.success(action === 'approve' ? 'Ad approved and added to rotation' : 'Submission rejected');
      fetchConfig();
    } catch {
      toast.error('Network error');
    }
  }

  async function handleToggle(index, currentEnabled) {
    try {
      const res = await authFetch(`/api/ads/live/${index}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !currentEnabled }),
      });
      if (!res.ok) { toast.error('Failed to update'); return; }
      toast.success(!currentEnabled ? 'Ad enabled' : 'Ad disabled');
      fetchConfig();
    } catch {
      toast.error('Network error');
    }
  }

  async function handleDelete(index, name) {
    if (!window.confirm(`Remove "${name || 'this ad'}" from the rotation? The logo image on ImgBB is not deleted.`)) return;
    try {
      const res = await authFetch(`/api/ads/live/${index}`, {
        method: 'DELETE',
      });
      if (!res.ok) { toast.error('Failed to remove'); return; }
      toast.success('Ad removed');
      fetchConfig();
    } catch {
      toast.error('Network error');
    }
  }

  async function handleClearAll() {
    if (!window.confirm('Clear EVERY ad from the rotation? The bar will fall back to the house ad.')) return;
    try {
      const res = await authFetch('/api/ads/clear', { method: 'POST' });
      if (!res.ok) { toast.error('Failed to clear'); return; }
      toast.success('All ads cleared');
      fetchConfig();
    } catch {
      toast.error('Network error');
    }
  }

  async function handleCreateLive(form, logo) {
    if (!logo) { toast.error('Please pick a logo image'); return; }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append('logo', logo);
    try {
      const res = await authFetch('/api/ads/live', {
        method: 'POST',
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || 'Publish failed'); return; }
      toast.success('Ad added to rotation');
      fetchConfig();
    } catch {
      toast.error('Network error');
    }
  }

  if (loading || !config) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <p className="text-slate-400">Loading…</p>
      </div>
    );
  }

  const live = config.live;
  const enabledCount = live.filter(a => a.enabled).length;

  return (
    <div className="min-h-screen bg-slate-950 p-6 text-slate-200">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-white text-xl font-bold">Ad Management</h1>
            <p className="text-slate-500 text-xs mt-1">
              Ads rotate every 30 seconds on a wall-clock cadence — every visitor sees the same ad at the same moment, page refreshes don't reset the rotation.
            </p>
          </div>
          {live.length > 0 && (
            <button
              onClick={handleClearAll}
              className="text-xs font-semibold px-3 py-1.5 rounded-full border border-red-500/30 text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-colors"
            >
              Clear all
            </button>
          )}
        </div>

        {/* Live rotation */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-white">
              Live Rotation
              <span className="text-slate-500 font-normal text-sm ml-2">
                {enabledCount} of {live.length} active
              </span>
            </h2>
          </div>

          {live.length === 0 ? (
            <p className="text-slate-500 text-sm italic">
              No ads in the rotation — the bar shows the house ad. Add one below.
            </p>
          ) : (
            <div className="space-y-3">
              {live.map((ad, index) => (
                <div
                  key={index}
                  className={`rounded-xl border p-4 ${
                    ad.enabled
                      ? 'bg-slate-800/40 border-slate-700'
                      : 'bg-slate-900 border-slate-800 opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-mono text-slate-500">#{index + 1}</span>
                        <h3 className="font-semibold text-white truncate">{ad.advertiser_name || '—'}</h3>
                        {!ad.enabled && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-700 text-slate-400">
                            Disabled
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleToggle(index, ad.enabled)}
                        className={`text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${
                          ad.enabled
                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-slate-700 hover:text-slate-300 hover:border-slate-600'
                            : 'bg-slate-800 text-slate-400 border-slate-700 hover:bg-emerald-500/10 hover:text-emerald-400 hover:border-emerald-500/30'
                        }`}
                      >
                        {ad.enabled ? 'Enabled' : 'Disabled'}
                      </button>
                      <button
                        onClick={() => handleDelete(index, ad.advertiser_name)}
                        className="text-xs font-semibold px-3 py-1 rounded-full border border-red-500/30 text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  <AdPreview ad={ad} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pending submission */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 mb-6">
          <h2 className="font-semibold text-white mb-4">Pending Submission</h2>
          {config.pending ? (
            <>
              <AdPreview ad={config.pending} />
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => handleApprove('approve')}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold py-2 rounded-lg"
                >
                  Approve & add to rotation
                </button>
                <button
                  onClick={() => handleApprove('reject')}
                  className="flex-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 text-sm font-semibold py-2 rounded-lg"
                >
                  Reject
                </button>
              </div>
            </>
          ) : (
            <p className="text-slate-500 text-sm">No pending submissions.</p>
          )}
        </div>

        {/* Create new ad (admin shortcut — skips the pending/approve flow) */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
          <h2 className="font-semibold text-white mb-1">Add New Ad to Rotation</h2>
          <p className="text-slate-500 text-xs mb-4">
            Upload an advertiser's artwork directly. The new ad enters the rotation as enabled.
          </p>
          <CreateAdForm onSubmit={handleCreateLive} />
        </div>
      </div>
    </div>
  );
}

function CreateAdForm({ onSubmit }) {
  const [form, setForm] = useState({
    advertiser_name: '',
    strapline: '',
    cta_label: '',
    cta_url: '',
    colour_1: '#1a1a2e',
    colour_2: '#1a1a2e',
    text_colour: '#ffffff',
  });
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);

  function change(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    await onSubmit(form, logo);
    setBusy(false);
    setForm({
      advertiser_name: '', strapline: '', cta_label: '', cta_url: '',
      colour_1: '#1a1a2e', colour_2: '#1a1a2e', text_colour: '#ffffff',
    });
    setLogo(null);
  }

  const inputCls = 'w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 text-sm focus:outline-none focus:border-emerald-500';
  const labelCls = 'block text-slate-400 text-xs font-medium mb-1';

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label className={labelCls}>Advertiser name</label>
        <input className={inputCls} value={form.advertiser_name}
               onChange={e => change('advertiser_name', e.target.value)} required />
      </div>
      <div>
        <label className={labelCls}>Strapline (max 80 chars)</label>
        <input className={inputCls} value={form.strapline} maxLength={80}
               onChange={e => change('strapline', e.target.value)} required />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>CTA label (max 20)</label>
          <input className={inputCls} value={form.cta_label} maxLength={20}
                 onChange={e => change('cta_label', e.target.value)} required />
        </div>
        <div>
          <label className={labelCls}>CTA URL</label>
          <input className={inputCls} type="url" value={form.cta_url}
                 onChange={e => change('cta_url', e.target.value)} required />
        </div>
      </div>
      <div>
        <label className={labelCls}>Logo (PNG / SVG / JPG — transparent background preferred)</label>
        <input type="file" accept="image/png,image/svg+xml,image/jpeg"
               onChange={e => setLogo(e.target.files[0] || null)}
               className="block w-full text-slate-400 text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-slate-700 file:text-slate-200 file:text-xs hover:file:bg-slate-600"
               required />
      </div>
      <div className="grid grid-cols-3 gap-3">
        {['colour_1', 'colour_2', 'text_colour'].map(k => (
          <div key={k}>
            <label className={labelCls}>
              {k === 'colour_1' ? 'Background 1' : k === 'colour_2' ? 'Background 2' : 'Text colour'}
            </label>
            <div className="flex items-center gap-2">
              <input type="color" value={form[k]} onChange={e => change(k, e.target.value)}
                     className="w-10 h-8 rounded cursor-pointer bg-transparent border-0" />
              <input type="text" value={form[k]} maxLength={7}
                     onChange={e => change(k, e.target.value)}
                     className={`${inputCls} font-mono`} />
            </div>
          </div>
        ))}
      </div>
      <button type="submit" disabled={busy}
              className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white font-semibold py-2.5 rounded-xl">
        {busy ? 'Publishing…' : 'Add to rotation'}
      </button>
    </form>
  );
}

function AdPreview({ ad }) {
  if (!ad || !ad.advertiser_name) {
    return <p className="text-slate-500 text-sm">No ad configured.</p>;
  }
  return (
    <div className="space-y-2 text-xs">
      <Row label="Strapline" value={ad.strapline} />
      <Row label="CTA" value={`${ad.cta_label} → ${ad.cta_url}`} />
      {ad.logo_url && <Row label="Logo" value={<a href={ad.logo_url} target="_blank" rel="noopener noreferrer" className="text-emerald-400 underline">View</a>} />}
      <Row label="Colours" value={
        <span className="flex items-center gap-2">
          <span className="inline-block w-4 h-4 rounded" style={{ background: ad.colour_1 || '#1a1a2e' }} title={ad.colour_1} />
          <span className="inline-block w-4 h-4 rounded" style={{ background: ad.colour_2 || '#1a1a2e' }} title={ad.colour_2} />
          <span className="inline-block w-4 h-4 rounded border border-slate-600" style={{ background: ad.text_colour || '#fff' }} title={ad.text_colour} />
        </span>
      } />
      <div className="mt-2 rounded-md overflow-hidden" style={{
        background: `linear-gradient(to right, ${ad.colour_1 || '#1a1a2e'}, ${ad.colour_2 || '#1a1a2e'})`,
        height: '40px',
      }}>
        <div className="flex items-center h-full px-3 gap-2">
          {ad.logo_url && <img src={ad.logo_url} alt="" className="h-6 w-auto object-contain flex-shrink-0" />}
          <p className="flex-1 text-center text-xs font-medium line-clamp-1" style={{ color: ad.text_colour || '#fff' }}>
            {ad.strapline}
          </p>
          {ad.cta_label && (
            <span className="bg-emerald-500 text-white text-[10px] font-semibold px-2 py-1 rounded">
              {ad.cta_label} →
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="text-slate-500 w-20 flex-shrink-0">{label}</span>
      <span className="text-slate-300 break-all">{value}</span>
    </div>
  );
}
